class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // cache overlay (can be replaced on re-render)
    this.overlay = this.querySelector('#CartDrawer-Overlay');

    // close on ESC
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // click backdrop to close
    this.overlay?.addEventListener('click', this.close.bind(this));

    // delegate close from any "Continue shopping" / custom close button
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.drawer__close, [data-close-cart]');
      if (btn) {
        e.preventDefault();
        this.close();
      }
    });

    this.setHeaderCartIconAccessibility();
  }

  // --- helpers ---------------------------------------------------------------

  recacheOverlay() {
    this.overlay = this.querySelector('#CartDrawer-Overlay');
    return this.overlay;
  }

  setOverlayVisible(on) {
    const ov = this.recacheOverlay();
    if (!ov) return;
    if (on) ov.classList.add('is-visible');
    else ov.classList.remove('is-visible');
  }

  // --------------------------------------------------------------------------

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // fade IN overlay via CSS
    this.setOverlayVisible(true);

    // kick drawer animation
    setTimeout(() => { this.classList.add('animate', 'active'); });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden', 'cart-drawer-active');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    // fade OUT overlay via CSS
    this.setOverlayVisible(false);

    // tiny safety net in case of paint/transition hiccup
    setTimeout(() => this.overlay?.classList.remove('is-visible'), 400);

    document.body.classList.remove('overflow-hidden', 'cart-drawer-active');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    // after re-render, overlay node may be new — re-bind and reopen
    setTimeout(() => {
      this.recacheOverlay();
      this.overlay?.addEventListener('click', this.close.bind(this));
      this.open();

      const total = document.getElementById('cart-total-price-ovarlay');
      total && total.classList.remove('loading');
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
